import { store } from '@/store/store';
import trackerAiApi from './trackerAiApi';
import './trackerEndpointsApi';
import { setTrackerDomainSlug } from './trackerDomainBody';
import type { TrackerDateRangeQuery } from './queryParams';
import { toApiError } from '../utils/api-errors';
import type { EnqueuePromptRunsResult, PromptRunBatchStatus } from '../types';

/** Syncs the request slug and drops in-flight queries from the previous domain. */
export function applyTrackerDomainSlug(slug: string | null | undefined): void {
  if (!setTrackerDomainSlug(slug)) return;
  store.dispatch(trackerAiApi.util.resetApiState());
}

/** Topic prompt table uses RTK, not ProjectContext. Refresh it after a run. */
export function invalidatePromptLists(): void {
  store.dispatch(trackerAiApi.util.invalidateTags(['Prompts']));
}

type PromptRunListener = (batchId: string, promptIds: string[]) => void;
const promptRunListeners = new Set<PromptRunListener>();
const promptRunClearListeners = new Set<() => void>();
const PROMPT_RUN_BATCH_KEY = 'trackerAi.promptRunBatchId';
const PROMPT_RUN_IDS_KEY = 'trackerAi.promptRunPromptIds';

/** Fires when a user run actually queues work (skipped-only runs stay quiet). */
export function subscribePromptRunBatch(listener: PromptRunListener): () => void {
  promptRunListeners.add(listener);
  return () => {
    promptRunListeners.delete(listener);
  };
}

export function subscribePromptRunCleared(listener: () => void): () => void {
  promptRunClearListeners.add(listener);
  return () => {
    promptRunClearListeners.delete(listener);
  };
}

/** Survives a reload so the progress bar can resubscribe to the same batch. */
export function readPromptRunBatch(): string | null {
  try {
    return sessionStorage.getItem(PROMPT_RUN_BATCH_KEY);
  } catch {
    return null;
  }
}

export function readPromptRunIds(): string[] {
  try {
    const raw = sessionStorage.getItem(PROMPT_RUN_IDS_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed.filter((id): id is string => typeof id === 'string')
      : [];
  } catch {
    return [];
  }
}

export function forgetPromptRunBatch(): void {
  try {
    sessionStorage.removeItem(PROMPT_RUN_BATCH_KEY);
    sessionStorage.removeItem(PROMPT_RUN_IDS_KEY);
  } catch {
    /* private mode */
  }
  for (const listener of promptRunClearListeners) listener();
}

function rememberPromptRunBatch(batchId: string): void {
  try {
    sessionStorage.setItem(PROMPT_RUN_BATCH_KEY, batchId);
  } catch {
    /* private mode */
  }
}

function rememberPromptRunIds(ids: string[]): void {
  try {
    sessionStorage.setItem(PROMPT_RUN_IDS_KEY, JSON.stringify(ids));
  } catch {
    /* private mode */
  }
}

function notifyPromptRunBatch(
  result: EnqueuePromptRunsResult,
  promptIds: string[] = [],
): void {
  if (result.createdItems <= 0 || !result.batchId) return;
  rememberPromptRunBatch(result.batchId);
  if (promptIds.length) rememberPromptRunIds(promptIds);
  for (const listener of promptRunListeners) listener(result.batchId, promptIds);
}

async function enqueuePromptRuns(body: {
  promptIds?: string[];
  topicId?: string;
  modelId?: string;
}): Promise<EnqueuePromptRunsResult> {
  const result = await runQuery<EnqueuePromptRunsResult>(
    trackerAiApi.endpoints.enqueuePromptRuns.initiate,
    body
  );
  notifyPromptRunBatch(result, body.promptIds ?? []);
  return result;
}

async function runQuery<T>(
  endpointInitiate: unknown,
  arg?: unknown
): Promise<T> {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (await (store.dispatch as any)(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (endpointInitiate as any)(arg, { forceRefetch: true })
    ).unwrap()) as T;
  } catch (error) {
    throw toApiError(error);
  }
}

/**
 * Imperative Tracker AI API client for contexts/callbacks.
 * Prefer generated RTK Query hooks in React components.
 */
export const trackerAiClient = {
  getProjects: () => runQuery(trackerAiApi.endpoints.getProjects.initiate),

  ensureProject: (body: {
    slug: string;
    domain: string;
    aliases?: string[];
  }) => runQuery(trackerAiApi.endpoints.ensureProject.initiate, body),

  deleteProject: (id: string) =>
    runQuery(trackerAiApi.endpoints.deleteProject.initiate, id),

  getProject: (id: string) =>
    runQuery(trackerAiApi.endpoints.getProject.initiate, id),

  getProjectActivityRange: (id: string) =>
    runQuery(trackerAiApi.endpoints.getProjectActivityRange.initiate, id),

  getProjectInsight: (id: string) =>
    runQuery(trackerAiApi.endpoints.getProjectInsight.initiate, id),

  getProjectPrompts: (projectId: string, options?: TrackerDateRangeQuery) =>
    runQuery(trackerAiApi.endpoints.getProjectPrompts.initiate, {
      projectId,
      ...options
    }),

  getProjectVisibilitySummary: (
    projectId: string,
    options?: TrackerDateRangeQuery
  ) =>
    runQuery(trackerAiApi.endpoints.getProjectVisibilitySummary.initiate, {
      projectId,
      ...options
    }),

  getProjectVisibilityTrend: (
    projectId: string,
    options?: TrackerDateRangeQuery
  ) =>
    runQuery(trackerAiApi.endpoints.getProjectVisibilityTrend.initiate, {
      projectId,
      ...options
    }),

  getProjectExecutions: (projectId: string, options?: TrackerDateRangeQuery) =>
    runQuery(trackerAiApi.endpoints.getProjectExecutions.initiate, {
      projectId,
      ...options
    }),

  getExecutionInsight: (executionId: string) =>
    runQuery(trackerAiApi.endpoints.getExecutionInsight.initiate, executionId),

  getDomains: (projectId: string) =>
    runQuery(trackerAiApi.endpoints.getDomains.initiate, projectId),

  createDomain: (
    projectId: string,
    body: {
      host: string;
      isPrimary?: boolean;
      isTrackingSource?: boolean;
      displayName?: string;
    }
  ) =>
    runQuery(trackerAiApi.endpoints.createDomain.initiate, {
      projectId,
      body
    }),

  updateDomain: (
    projectId: string,
    domainId: string,
    body: { host: string }
  ) =>
    runQuery(trackerAiApi.endpoints.updateDomain.initiate, {
      projectId,
      domainId,
      body
    }),

  getBrandAliases: (projectId: string) =>
    runQuery(trackerAiApi.endpoints.getBrandAliases.initiate, projectId),

  createBrandAlias: (
    projectId: string,
    body: { name: string; language?: string; isPrimary?: boolean }
  ) =>
    runQuery(trackerAiApi.endpoints.createBrandAlias.initiate, {
      projectId,
      body
    }),

  updateBrandAlias: (
    projectId: string,
    aliasId: string,
    body: { name: string }
  ) =>
    runQuery(trackerAiApi.endpoints.updateBrandAlias.initiate, {
      projectId,
      aliasId,
      body
    }),

  deleteBrandAlias: (projectId: string, aliasId: string) =>
    runQuery(trackerAiApi.endpoints.deleteBrandAlias.initiate, {
      projectId,
      aliasId
    }),

  getCompetitors: (projectId: string) =>
    runQuery(trackerAiApi.endpoints.getCompetitors.initiate, projectId),

  getCompetitorIntelligence: (
    projectId: string,
    options?: TrackerDateRangeQuery
  ) =>
    runQuery(trackerAiApi.endpoints.getCompetitorIntelligence.initiate, {
      projectId,
      ...options
    }),

  createCompetitor: (
    projectId: string,
    body: { brandName: string; websiteUrl: string; aliases?: string[] }
  ) =>
    runQuery(trackerAiApi.endpoints.createCompetitor.initiate, {
      projectId,
      body
    }),

  createCompetitorFromExtracted: (
    projectId: string,
    body: { brandName: string; detectedHost?: string }
  ) =>
    runQuery(trackerAiApi.endpoints.createCompetitorFromExtracted.initiate, {
      projectId,
      body
    }),

  updateCompetitor: (
    projectId: string,
    competitorId: string,
    body: { brandName?: string; websiteUrl?: string; aliases?: string[] }
  ) =>
    runQuery(trackerAiApi.endpoints.updateCompetitor.initiate, {
      projectId,
      competitorId,
      body
    }),

  deleteCompetitor: (projectId: string, competitorId: string) =>
    runQuery(trackerAiApi.endpoints.deleteCompetitor.initiate, {
      projectId,
      competitorId
    }),

  getSourceInsightsSummary: (
    projectId: string,
    options?: TrackerDateRangeQuery
  ) =>
    runQuery(trackerAiApi.endpoints.getSourceInsightsSummary.initiate, {
      projectId,
      ...options
    }),

  getSourceInsightsDomains: (
    projectId: string,
    options?: TrackerDateRangeQuery & { page?: number; pageSize?: number }
  ) =>
    runQuery(trackerAiApi.endpoints.getSourceInsightsDomains.initiate, {
      projectId,
      ...options
    }),

  getSourceInsightsTypes: (
    projectId: string,
    options?: TrackerDateRangeQuery
  ) =>
    runQuery(trackerAiApi.endpoints.getSourceInsightsTypes.initiate, {
      projectId,
      ...options
    }),

  getSourceInsightsTrend: (
    projectId: string,
    options?: TrackerDateRangeQuery & { topN?: number }
  ) =>
    runQuery(trackerAiApi.endpoints.getSourceInsightsTrend.initiate, {
      projectId,
      ...options
    }),

  getSourceFilterOptions: (
    projectId: string,
    options?: TrackerDateRangeQuery
  ) =>
    runQuery(trackerAiApi.endpoints.getSourceFilterOptions.initiate, {
      projectId,
      ...options
    }),

  getPromptSourceInsights: (
    promptId: string,
    options?: TrackerDateRangeQuery
  ) =>
    runQuery(trackerAiApi.endpoints.getPromptSourceInsights.initiate, {
      promptId,
      ...options
    }),

  getBrandSourceInsights: (
    projectId: string,
    brandKey: string,
    options?: TrackerDateRangeQuery
  ) =>
    runQuery(trackerAiApi.endpoints.getBrandSourceInsights.initiate, {
      projectId,
      brandKey,
      ...options
    }),

  getCompetitorSourceGap: (
    projectId: string,
    options?: TrackerDateRangeQuery
  ) =>
    runQuery(trackerAiApi.endpoints.getCompetitorSourceGap.initiate, {
      projectId,
      ...options
    }),

  getTopics: (projectId: string) =>
    runQuery(trackerAiApi.endpoints.getTopics.initiate, projectId),

  createTopic: (
    projectId: string,
    name: string,
    language?: string,
    description?: string,
    marketCountry?: string
  ) =>
    runQuery(trackerAiApi.endpoints.createTopic.initiate, {
      projectId,
      name,
      language,
      description,
      marketCountry
    }),

  updateTopic: (projectId: string, topicId: string, body: { name: string }) =>
    runQuery(trackerAiApi.endpoints.updateTopic.initiate, {
      projectId,
      topicId,
      body
    }),

  deleteTopics: (projectId: string, topicIds: string[], deletePrompts = false) =>
    runQuery(trackerAiApi.endpoints.deleteTopics.initiate, {
      projectId,
      topicIds,
      deletePrompts
    }),

  suggestTopicPrompts: (topicId: string, modelId?: string) =>
    runQuery(trackerAiApi.endpoints.suggestTopicPrompts.initiate, {
      topicId,
      modelId
    }),

  batchCreatePrompts: (
    topicId: string,
    modelIds: string[],
    prompts: string[]
  ): Promise<{ ids: string[] } | null> =>
    runQuery<{ ids: string[] } | null>(
      trackerAiApi.endpoints.batchCreatePrompts.initiate,
      {
        topicId,
        modelIds,
        prompts
      }
    ),

  getPromptInsight: (id: string, options?: TrackerDateRangeQuery) =>
    runQuery(trackerAiApi.endpoints.getPromptInsight.initiate, {
      promptId: id,
      ...options
    }),

  getPromptExecutionHistory: (id: string, options?: TrackerDateRangeQuery) =>
    runQuery(trackerAiApi.endpoints.getPromptExecutionHistory.initiate, {
      promptId: id,
      ...options
    }),

  getPromptBrandAggregation: (id: string, options?: TrackerDateRangeQuery) =>
    runQuery(trackerAiApi.endpoints.getPromptBrandAggregation.initiate, {
      promptId: id,
      ...options
    }),

  getPromptOverview: (id: string, options?: TrackerDateRangeQuery) =>
    runQuery(trackerAiApi.endpoints.getPromptOverview.initiate, {
      promptId: id,
      ...options
    }),

  createProjectPrompt: (
    projectId: string,
    body: {
      text: string;
      modelId: string;
      modelIds?: string[];
      mode?: 'quick' | 'structured';
      topicId?: string;
      newTopicName?: string;
    }
  ) =>
    runQuery(trackerAiApi.endpoints.createProjectPrompt.initiate, {
      projectId,
      body
    }),

  createPrompt: (
    topicId: string,
    text: string,
    modelId: string,
    modelIds?: string[]
  ) =>
    runQuery(trackerAiApi.endpoints.createPrompt.initiate, {
      topicId,
      text,
      modelId,
      modelIds
    }),

  getPromptRunBatch: (batchId: string) =>
    runQuery<PromptRunBatchStatus>(
      trackerAiApi.endpoints.getPromptRunBatch.initiate,
      batchId
    ),

  enqueuePromptRuns,

  runPrompt: (promptId: string, modelId?: string) =>
    enqueuePromptRuns({
      promptIds: [promptId],
      ...(modelId ? { modelId } : {})
    }),

  updatePrompt: (
    promptId: string,
    body: { text?: string; modelId?: string; modelIds?: string[] }
  ) =>
    runQuery(trackerAiApi.endpoints.updatePrompt.initiate, {
      promptId,
      body
    }),

  deletePrompt: (promptIds: string[]) =>
    runQuery(trackerAiApi.endpoints.deletePrompt.initiate, promptIds),

  reextractBrands: (promptIds: string[]) =>
    runQuery(trackerAiApi.endpoints.reextractBrands.initiate, promptIds),

  getPublicModels: () =>
    runQuery(trackerAiApi.endpoints.getPublicModels.initiate)
};
